import { skipToken, useQuery } from '@tanstack/react-query';

import { Collections } from '@air/api';
import { type ApiResponse, callAirApi } from '@air/utils-api';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

type GetCollectionResponse = ApiResponse<typeof Collections.get>;

export type CollectionInput = {
  collectionId: string;
};

export const COLLECTION_KEY = 'COLLECTION_KEY';
export type CollectionKey = [typeof COLLECTION_KEY, CollectionInput];

export const getCollectionKey = (params: CollectionInput): CollectionKey => [
  COLLECTION_KEY,
  { collectionId: params.collectionId },
];

export type UseGetCollectionParams = CollectionInput & {
  /**
   * Skip the fetch for a `collectionId` no server knows — today, a share-link sub-page that exists
   * only in the editor's unsaved draft. Defaults to fetching.
   */
  enabled?: boolean;
};

export const useGetCollection = ({ collectionId, enabled = true }: UseGetCollectionParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  return useQuery({
    queryKey: getCollectionKey({ collectionId }),
    queryFn:
      collectionId && workspaceId
        ? () => callAirApi<GetCollectionResponse>(() => Collections.get({ workspaceId, collectionId }))
        : skipToken,
    enabled: enabled && !!collectionId && !!workspaceId,
  });
};
